import type { ContractBase } from '@/types/policy/PolicyTypes'
import type { DataBlock } from 'de-zurich-ui-library'
import type {
  LegalPerson,
  LegalPersonName,
  NaturalPerson as PartnerApiNaturalPerson,
  NaturalPersonName,
  PartnerAddress,
  PhysicalAddress,
  PostOfficeBox
} from 'partner-api'

export type PersonUnion = LegalPerson | PartnerApiNaturalPerson
export type PersonNameUnion = LegalPersonName | NaturalPersonName
export type PostalAddressUnion = PhysicalAddress | PostOfficeBox
export type LifePartnerAddress = PartnerAddress

export enum ProductClassEnum {
  FONDS = 'productClass.fonds',
  PENSION = 'productClass.pension',
  CAPITAL_LIFE = 'productClass.capitalLife',
  RISK_LIFE = 'productClass.riskLife',
  EXISTENCE = 'productClass.existence',
  RUE_RUP = 'productClass.rueRup',
  RIESTER = 'productClass.riester',
  UNKNOWN = 'productClass.unknown'
}

export interface LifePartner {
  partnerAddresses: LifePartnerAddress[]
  partnerId?: number
  person: PersonUnion
}

export type LifeProductType =
  | 'FONDS'
  | 'CAPITAL_LIFE'
  | 'RISK_LIFE'
  | 'PENSION'
  | 'EXISTENCE'
  | 'RIESTER'
  | 'RUE_RUP'
  | 'UNKNOWN'

export type LifeContract = RecursivePartial<{
  contractBase: ContractBase
  tariff: Tariff
  dynamic: Dynamic
  insuredPersons: InsuredPerson[]
  beneficiaries: Beneficiaries[]
  thirdPartyRights: ThirdPartyRights[]
  insuredCoverages: InsuredCoverage[]
}>

type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : // eslint-disable-next-line @typescript-eslint/no-explicit-any
      T[P] extends Record<string, any>
      ? RecursivePartial<T[P]>
      : T[P]
}

export type ProductType =
  | 'FONDS'
  | 'CAPITAL_LIFE'
  | 'RISK_LIFE'
  | 'PENSION'
  | 'EXISTENCE'
  | 'RIESTER'
  | 'RUE_RUP'
  | 'UNKNOWN'

export type Tariff = RecursivePartial<{
  productName: string
  productType: ProductType
}>

export type Dynamic = RecursivePartial<{
  lastDynamic: string
  percentage: number
}>

export type InsuredPerson = RecursivePartial<
  Sequenced & {
    lastName: string
    firstName: string
    birthdate: string
  }
>

export type Beneficiaries = RecursivePartial<
  Sequenced & {
    type: string
    code: string
    typeOfService: string
    partnerCategory: string
    partnerCategoryCode: string
    person: {
      firstName: string
      lastName: string
      birthdate: string
    }
  }
>

export type ThirdPartyRights = RecursivePartial<
  Sequenced & {
    name: string
    street: string
    zipCode: string
    city: string
    assignmentLetterDate: string
  }
>

interface Sequenced {
  sequence: number
}

export type InsuredCoverage = {
  label: string
} & (
  | {
      type: 'riskLifeInsurance'
      coverageDeath: number
    }
  | {
      type: 'capitalLifeInsurance'
      coverageDeath: number
      coverageSurvival: number
    }
  | {
      type: 'incapacity'
      expectedMonthlyIncapacityPension: number
    }
  | {
      type: 'pension'
      payoutPeriod: PayoutPeriod
      expectedMonthlyPension: number
      guaranteedCapital: number
      pensionPaymentMethod: string
    }
)

export type PayoutPeriod = RecursivePartial<{
  startDate: string
  durationInYears: number
}>

export enum StatusColorEnum {
  GREEN = 'GREEN',
  RED = 'RED',
  GREY = 'GREY'
}

export enum PolicyStatus {
  ACTIVE = 'ACTIVE',
  SHORT_TERM = 'SHORT_TERM',
  SUBMITTED = 'SUBMITTED',
  CANCELED = 'CANCELED',
  NON_CONTRIBUTORY_A = 'NON_CONTRIBUTORY_A',
  NON_CONTRIBUTORY_B = 'NON_CONTRIBUTORY_B',
  NON_CONTRIBUTORY_C = 'NON_CONTRIBUTORY_C',
  CONTRIBUTION_DEFERRAL = 'CONTRIBUTION_DEFERRAL',
  OTHER = 'OTHER',
  CAPITAL_SEVERANCE = 'CAPITAL_SEVERANCE',
  POWER_CASE = 'POWER_CASE',
  TERMINATED = 'TERMINATED',
  COMMISSIONED = 'COMMISSIONED'
}

export type NaturalPersonTitleEnum =
  (typeof NaturalPersonTitleEnum)[keyof typeof NaturalPersonTitleEnum]

export type NaturalPersonNamePrefixEnum =
  (typeof NaturalPersonNamePrefixEnum)[keyof typeof NaturalPersonNamePrefixEnum]

export type NaturalPersonNameSuffixEnum =
  (typeof NaturalPersonNameSuffixEnum)[keyof typeof NaturalPersonNameSuffixEnum]

export const NaturalPersonNameSuffixEnum = {
  Jun: 'jun.',
  Sen: 'sen.'
} as const
export const NaturalPersonNamePrefixEnum = {
  Am: 'am',
  An: 'an',
  AnDer: 'an der',
  Aus: 'aus',
  AusDem: 'aus dem',
  AusDen: 'aus den',
  AusDer: 'aus der',
  Bel: 'bel',
  D: "d'",
  Da: 'da',
  Dal: 'dal',
  Das: 'das',
  De: 'de',
  Del: 'del',
  DeLa: 'de la',
  Della: 'della',
  Delle: 'delle',
  Dem: 'dem',
  Den: 'den',
  Der: 'der',
  Des: 'des',
  Di: 'di',
  Do: 'do',
  Dos: 'dos',
  Du: 'du',
  El: 'El',
  In: 'in',
  InDen: 'in den',
  InDer: 'in der',
  InT: "in t'",
  L: "l'",
  La: 'la',
  Le: 'Le',
  Les: 'les',
  Lo: 'lo',
  Mac: 'Mac',
  Mc: 'Mc',
  Op: 'op',
  OpDe: 'op de',
  OpDen: 'op den',
  OpT: "op t'",
  OpTen: 'op ten',
  Saint: 'Saint',
  Si: 'si',
  Skt: 'Skt.',
  Ten: 'ten',
  To: 'to',
  Van: 'van',
  VanDe: 'van de',
  VanDem: 'van dem',
  VanDen: 'van den',
  VanDer: 'van der',
  VanT: "van t'",
  Vom: 'vom',
  Von: 'von',
  VonDem: 'von dem',
  VonDen: 'von den',
  VonDer: 'von der',
  VonZur: 'von zur',
  Vor: 'vor',
  VorDem: 'vor dem',
  VorDen: 'vor den',
  VorDer: 'vor der',
  Vu: 'vu',
  Zu: 'zu',
  ZuDem: 'zu dem',
  ZuDen: 'zu den',
  ZuDer: 'zu der',
  Zum: 'zum',
  Zur: 'zur'
} as const

export const NaturalPersonTitleEnum = {
  Mr: 'mr',
  Mrs: 'mrs',
  Miss: 'miss',
  Unspecified: 'unspecified'
} as const

export type NaturalPersonDeceasedEnum =
  (typeof NaturalPersonDeceasedEnum)[keyof typeof NaturalPersonDeceasedEnum]

export const NaturalPersonDeceasedEnum = {
  Yes: 'yes',
  No: 'no'
} as const

export type NaturalPersonEmploymentTypeEnum =
  (typeof NaturalPersonEmploymentTypeEnum)[keyof typeof NaturalPersonEmploymentTypeEnum]

export const NaturalPersonEmploymentTypeEnum = {
  Unemployed: 'unemployed',
  Employee: 'employee',
  EmployeeInPublicService: 'employeeInPublicService',
  Official: 'official',
  SelfEmployed: 'selfEmployed',
  Freelancer: 'freelancer',
  ExecutiveEmployee: 'executiveEmployee',
  InEducation: 'inEducation',
  Pensioner: 'pensioner',
  HouseWifeHouseMan: 'houseWifeHouseMan'
} as const

export type NaturalPersonFamilyStatusEnum =
  (typeof NaturalPersonFamilyStatusEnum)[keyof typeof NaturalPersonFamilyStatusEnum]

export const NaturalPersonFamilyStatusEnum = {
  SingleWithoutChild: 'singleWithoutChild',
  CoupleWithoutChildren: 'coupleWithoutChildren',
  CoupleWithChild: 'coupleWithChild',
  SingleWithChild: 'singleWithChild'
} as const

export type NaturalPersonTitleOfNobilityEnum =
  (typeof NaturalPersonTitleOfNobilityEnum)[keyof typeof NaturalPersonTitleOfNobilityEnum]

export const NaturalPersonTitleOfNobilityEnum = {
  Baron: 'Baron',
  Baroness: 'Baroness',
  Baronesse: 'Baronesse',
  Baronin: 'Baronin',
  Edle: 'Edle',
  Edler: 'Edler',
  Freifrau: 'Freifrau',
  Freiherr: 'Freiherr',
  Freiin: 'Freiin',
  Frst: 'Fürst',
  Frstin: 'Fürstin',
  Grfin: 'Gräfin',
  Graf: 'Graf',
  Herzog: 'Herzog',
  Herzogin: 'Herzogin',
  Komtesse: 'Komtesse',
  Markgrfin: 'Markgräfin',
  Markgraf: 'Markgraf',
  Prinz: 'Prinz',
  Prinzessin: 'Prinzessin',
  Ritter: 'Ritter'
} as const

export interface NaturalPerson {
  title?: NaturalPersonTitleEnum
  lastName?: string
  firstName?: string
  nameAtBirth?: string
  namePrefix?: NaturalPersonNamePrefixEnum
  nameSuffix?: NaturalPersonNameSuffixEnum
  academicTitle?: NaturalPersonTitleOfNobilityEnum
  titleOfNobility?: NaturalPersonTitleOfNobilityEnum
  otherTitle?: string
  familyStatus?: NaturalPersonFamilyStatusEnum
  placeOfBirth?: string
  employmentType?: NaturalPersonEmploymentTypeEnum
  professionCode?: string
  nationalityCode?: string
  dateOfBirth?: string
  dateOfDeath?: string
  deceased?: NaturalPersonDeceasedEnum
  politicalExposedPerson?: boolean
}

export type SequencedDataBlock = DataBlock & Sequenced

export const StreetAddressTypeEnum = {
  GermanStreetAddress: 'germanStreetAddress',
  InternationalStreetAddress: 'internationalStreetAddress'
} as const

export type StreetAddressTypeEnum =
  (typeof StreetAddressTypeEnum)[keyof typeof StreetAddressTypeEnum]

export interface StreetAddress {
  city?: string
  type?: StreetAddressTypeEnum
  cityDetail?: string
  streetDetail?: string
}

export interface Address {
  communications?: Array<Communication>
  streetAddress?: StreetAddress
  countryCode?: string
  correspondenceLanguage?: AddressCorrespondenceLanguageEnum
  correlationId?: string
  nonResidentialAddress?: NonResidentialAddress
  id?: number
  addressStatus?: AddressAddressStatusEnum
  additionalAddressLine1?: string
  additionalAddressLine2?: string
}

export interface Communication {
  usage?: CommunicationUsageEnum
  index?: number
  correlationId?: string
  type: CommunicationTypeEnum
}

export type CommunicationUsageEnum =
  (typeof CommunicationUsageEnum)[keyof typeof CommunicationUsageEnum]
export const CommunicationTypeEnum = {
  LandlinePhone: 'landlinePhone',
  Email: 'email',
  CellPhone: 'cellPhone',
  FaxNumber: 'faxNumber'
} as const

export type CommunicationTypeEnum =
  (typeof CommunicationTypeEnum)[keyof typeof CommunicationTypeEnum]

export const CommunicationUsageEnum = {
  Private: 'private',
  Business: 'business',
  Unknown: 'unknown'
} as const

export const AddressCorrespondenceLanguageEnum = {
  German: 'german',
  Spanish: 'spanish',
  French: 'french',
  English: 'english',
  Italian: 'italian'
} as const

export type AddressCorrespondenceLanguageEnum =
  (typeof AddressCorrespondenceLanguageEnum)[keyof typeof AddressCorrespondenceLanguageEnum]
export const AddressAddressStatusEnum = {
  RegistrationOfficeRequestSent: 'registrationOfficeRequestSent',
  AddressUnkown: 'addressUnkown',
  PostChange: 'postChange',
  FutureChange: 'futureChange',
  BankRequest: 'bankRequest',
  Pending: 'pending'
} as const

export type AddressAddressStatusEnum =
  (typeof AddressAddressStatusEnum)[keyof typeof AddressAddressStatusEnum]

export interface NonResidentialAddress {
  city?: string
  postalCode?: string
  type: NonResidentialAddressTypeEnum
}

export const NonResidentialAddressTypeEnum = {
  MajorCustomerAddress: 'majorCustomerAddress',
  POBoxAddress: 'pOBoxAddress'
} as const

export type NonResidentialAddressTypeEnum =
  (typeof NonResidentialAddressTypeEnum)[keyof typeof NonResidentialAddressTypeEnum]

export interface GermanStreetAddress extends StreetAddress {
  streetName?: string
  houseNumber?: string
  postalCode?: string
}
